import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Typography, message } from 'antd';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import api from '../../api/client';
import useAuthStore from '../../store/authStore';
import { useOperatorGateStore } from '../../store/operatorGateStore';

const { Text, Title } = Typography;

interface OperatorBrief {
  id: number;
  display_name: string | null;
}

type GateStep = 'user' | 'pin';

/**
 * Экран выбора активного оператора рабочего места (спека 2026-09-25).
 * Два шага: сначала выбор оператора кнопкой, затем ввод PIN
 * (4-я цифра = подтверждение). Кнопка «Назад» возвращает к выбору.
 * Показывается: утром/после сброса (operator_required) и вручную из шапки.
 * После успешного PIN-переключения — полная перезагрузка страницы
 * (перечитывает /me, буфер сканов, переподключает WebSocket чата).
 */
const OperatorGate: React.FC = () => {
  const { user, fetchMe, switchOperator, logout } = useAuthStore();
  const { manualOpen, closeManual, requiredTick } = useOperatorGateStore();
  const [operators, setOperators] = useState<OperatorBrief[]>([]);
  const [step, setStep] = useState<GateStep>('user');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const isWorkplace = !!user?.workplace;
  const visible = isWorkplace && (!!user?.operator_required || manualOpen);

  // 401 operator_required от любого API — перечитываем /me, gate откроется
  useEffect(() => {
    if (requiredTick > 0 && isWorkplace) {
      fetchMe();
    }
  }, [requiredTick, isWorkplace, fetchMe]);

  useEffect(() => {
    if (!visible) return;
    setStep('user');
    setSelectedId(null);
    setPin('');
    api.get('/auth/workplace-operators').then((res) => {
      setOperators(res.data || []);
    }).catch(() => {
      message.error('Не удалось загрузить список операторов');
    });
  }, [visible]);

  if (!visible) return null;

  const canClose = manualOpen && !user?.operator_required;
  const lastId = Number(localStorage.getItem('last_operator_id'));
  const selected = operators.find((o) => o.id === selectedId) || null;

  const pickOperator = (id: number) => {
    setSelectedId(id);
    setPin('');
    setStep('pin');
  };

  const goBack = () => {
    setPin('');
    setStep('user');
  };

  const submitPin = async (operatorId: number, pinValue: string) => {
    setLoading(true);
    try {
      await switchOperator(operatorId, pinValue);
      closeManual();
      window.location.reload();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Ошибка переключения');
      setPin('');
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable={canClose}
      onCancel={canClose ? closeManual : undefined}
      maskClosable={false}
      centered
      width={420}
    >
      {step === 'user' ? (
        <>
          <div style={{ textAlign: 'center', paddingTop: 8 }}>
            <Title level={4} style={{ marginBottom: 4 }}>Кто работает?</Title>
            <Text type="secondary">{user?.workplace}</Text>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              marginTop: 20,
            }}
          >
            {operators.map((o) => (
              <Button
                key={o.id}
                size="large"
                icon={<UserOutlined />}
                type={o.id === lastId ? 'primary' : 'default'}
                ghost={o.id === lastId}
                onClick={() => pickOperator(o.id)}
                style={{ height: 56, fontSize: 18 }}
              >
                {o.display_name || `#${o.id}`}
              </Button>
            ))}
            {operators.length === 0 && (
              <Text type="secondary" style={{ textAlign: 'center' }}>
                Нет операторов с PIN. Задайте PIN в профиле или на странице пользователей.
              </Text>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', paddingTop: 4 }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              disabled={loading}
            >
              Назад
            </Button>
          </div>
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {selected?.display_name || 'Оператор'}
            </Title>
            <Text type="secondary">Введите PIN</Text>
          </div>
          <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 8 }}>
            <Input.OTP
              key={selectedId ?? 'none'}
              length={4}
              mask="•"
              size="large"
              autoFocus
              value={pin}
              onChange={setPin}
              disabled={loading}
              onInput={(v) => {
                const val = v.join('');
                if (val.length === 4 && selectedId) submitPin(selectedId, val);
              }}
            />
          </div>
        </>
      )}

      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Button type="link" size="small" onClick={logout}>
          Выйти из учётной записи
        </Button>
      </div>
    </Modal>
  );
};

export default OperatorGate;
